/**
 * NavSidebar — collapsible left-side navigation with department sub-nav.
 *
 * Uses Framer Motion for the collapse/expand animation.
 * Calls onSelectSkill when a skill item is clicked.
 */

import { useState, type ComponentType, type KeyboardEvent } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  PanelLeftClose,
  PanelLeftOpen,
  ChevronDown,
  ChevronRight,
  Search,
  UserPlus,
  Scale,
  BarChart3,
  Users,
  TrendingUp,
  Cog,
  Code2,
  Lightbulb,
  Folder,
  type LucideProps,
} from 'lucide-react'

// ── Types ─────────────────────────────────────────────────────────────────────

export interface Skill {
  id: string
  label: string
}

export interface Department {
  id: string
  label: string
  skills: Skill[]
}

export interface NavSidebarProps {
  departments: Department[]
  activeSkillId: string | null
  onSelectSkill: (skillId: string) => void
  /** Controlled expanded state. Defaults to true (expanded). */
  expanded?: boolean
  onToggleExpanded?: () => void
}

// ── Defaults ──────────────────────────────────────────────────────────────────

const DEPT_ICONS: Record<string, ComponentType<LucideProps>> = {
  research:    Search,
  recruitment: UserPlus,
  hr:          Users,
  legal:       Scale,
  finance:     BarChart3,
  sales:       TrendingUp,
  ops:         Cog,
  tech:        Code2,
  product:     Lightbulb,
}

// ── Sidebar widths ────────────────────────────────────────────────────────────

const EXPANDED_WIDTH   = 240
const COLLAPSED_WIDTH  = 56

// ── Component ─────────────────────────────────────────────────────────────────

export function NavSidebar({
  departments,
  activeSkillId,
  onSelectSkill,
  expanded: expandedProp,
  onToggleExpanded,
}: NavSidebarProps) {
  // Allow uncontrolled usage
  const [internalExpanded, setInternalExpanded] = useState(true)
  const isExpanded = expandedProp !== undefined ? expandedProp : internalExpanded

  function toggle() {
    if (onToggleExpanded) {
      onToggleExpanded()
    } else {
      setInternalExpanded((v) => !v)
    }
  }

  // Which department sections are collapsed
  const [collapsedDepts, setCollapsedDepts] = useState<Record<string, boolean>>({})

  function toggleDept(deptId: string) {
    setCollapsedDepts((prev) => ({ ...prev, [deptId]: !prev[deptId] }))
  }

  return (
    <motion.nav
      className="[grid-area:sidebar] flex flex-col bg-[#0b1220] border-r border-[rgba(148,163,184,0.10)] overflow-hidden relative flex-shrink-0 min-h-0"
      aria-label="Department skills"
      animate={{ width: isExpanded ? EXPANDED_WIDTH : COLLAPSED_WIDTH }}
      transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
      style={{ width: isExpanded ? EXPANDED_WIDTH : COLLAPSED_WIDTH }}
    >
      {/* ── Toggle button ── */}
      <button
        className="absolute top-3 -right-3 w-6 h-6 rounded-full border border-[rgba(148,163,184,0.18)] bg-[#101a2b] text-[#8fa3bd] flex items-center justify-center cursor-pointer z-[100] text-[13px] leading-none transition-[background,color,box-shadow] duration-[120ms] p-0 hover:bg-[rgba(6,182,212,0.14)] hover:text-[#06b6d4] hover:shadow-[0_0_8px_rgba(6,182,212,0.30)] hover:border-[rgba(6,182,212,0.3)]"
        aria-label={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
        aria-expanded={isExpanded}
        onClick={toggle}
        title={isExpanded ? 'Collapse' : 'Expand'}
      >
        <span
          className="inline-block text-base leading-none [transition:transform_0.2s_cubic-bezier(0.16,1,0.3,1)]"
          aria-hidden="true"
        >
          {isExpanded ? <PanelLeftClose size={16} /> : <PanelLeftOpen size={16} />}
        </span>
      </button>

      {/* ── Department list ── */}
      <div className="nav-body-scroll flex-1 overflow-y-auto py-3 pb-6">
        {departments.map((dept) => {
          const isOpen = !collapsedDepts[dept.id]
          const Icon   = DEPT_ICONS[dept.id] ?? Folder

          return (
            <div className="mb-0.5" key={dept.id}>
              {/* Department header */}
              <div
                className="flex items-center gap-2 py-2 px-3 pb-1 cursor-pointer select-none min-h-[32px] whitespace-nowrap overflow-hidden hover:[&_.dept-label]:text-[#8fa3bd]"
                role="button"
                tabIndex={0}
                aria-expanded={isOpen}
                onClick={() => isExpanded && toggleDept(dept.id)}
                onKeyDown={(e: KeyboardEvent) => {
                  if (e.key === 'Enter' || e.key === ' ') toggleDept(dept.id)
                }}
                title={dept.label}
              >
                <span className="text-sm flex-shrink-0 w-5 text-center" aria-hidden="true">
                  <Icon size={16} />
                </span>
                <AnimatePresence initial={false}>
                  {isExpanded && (
                    <motion.span
                      className="dept-label text-[10px] font-extrabold tracking-[0.14em] uppercase text-[#4a6280] flex-1 overflow-hidden text-ellipsis"
                      initial={{ opacity: 0, x: -6 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -6 }}
                      transition={{ duration: 0.15 }}
                    >
                      {dept.label}
                    </motion.span>
                  )}
                </AnimatePresence>
                {isExpanded && (
                  <span
                    className={`text-[8px] text-[#4a6280] flex-shrink-0 transition-transform duration-150${isOpen ? ' rotate-90' : ''}`}
                    aria-hidden="true"
                  >
                    {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </span>
                )}
              </div>

              {/* Skill list */}
              <AnimatePresence initial={false}>
                {isExpanded && isOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
                    style={{ overflow: 'hidden' }}
                  >
                    {dept.skills.map((skill) => {
                      const isActive = skill.id === activeSkillId
                      return (
                        <div
                          key={skill.id}
                          className={`flex items-center gap-2.5 py-1.5 px-3 pl-5 cursor-pointer text-[12.5px] whitespace-nowrap overflow-hidden text-ellipsis rounded-none transition-[background,color] duration-100 ${
                            isActive
                              ? 'bg-[rgba(6,182,212,0.14)] text-[#06b6d4]'
                              : 'text-[#8fa3bd] hover:bg-[rgba(22,36,58,0.8)] hover:text-[#e2ecf9]'
                          }`}
                          role="button"
                          tabIndex={0}
                          aria-pressed={isActive}
                          onClick={() => onSelectSkill(skill.id)}
                          onKeyDown={(e: KeyboardEvent) => {
                            if (e.key === 'Enter' || e.key === ' ') onSelectSkill(skill.id)
                          }}
                          title={skill.label}
                        >
                          <span
                            className={`w-[5px] h-[5px] rounded-full flex-shrink-0 ${
                              isActive
                                ? 'bg-[#06b6d4] shadow-[0_0_5px_rgba(6,182,212,0.6)]'
                                : 'bg-[rgba(148,163,184,0.18)]'
                            }`}
                            aria-hidden="true"
                          />
                          <span className="overflow-hidden text-ellipsis">{skill.label}</span>
                        </div>
                      )
                    })}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )
        })}
      </div>
    </motion.nav>
  )
}
